'use client';

import { useEffect, useRef, useState } from 'react';
import { useFetch } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import { clawsListSessions } from '../apis';
import type { ClawV1Session } from '../types';

export interface SessionDetailState {
  /** session detail 数据 */
  sessionDetail: ClawV1Session | null;
  /** detail 请求完成且 session 存在 */
  isReady: boolean;
  /** 正在加载 */
  isLoading: boolean;
  /** session 无法访问（不存在、claw 不匹配、接口异常等统一视为不可达） */
  isNotFound: boolean;
}

/**
 * 获取单个 session 的详情。
 * 使用 clawsListSessions 传入 session_ids 拉取，作为 history 等下游请求的前置条件。
 * 后端对不存在的 session/claw 返回空数组而非错误码，因此统一以返回数据是否为空判定可达性。
 */
export const useSessionDetail = (sessionId: string | null): SessionDetailState => {
  const activeClaw = useStore((state) => state.activeClaw);
  const storeApi = useStoreApi();

  const [sessionDetail, setSessionDetail] = useState<ClawV1Session | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);

  const requestIdRef = useRef(0);

  const { runAsync: fetchSessionDetail } = useFetch(
    ({ clawId, sessionId: sid }: { clawId: string; sessionId: string }) =>
      clawsListSessions({
        query: {
          claw_id: clawId,
          session_ids: [sid],
          'pager.page': 0,
          'pager.pagesz': 1,
        },
      }),
    { manual: true },
  );

  useEffect(() => {
    setSessionDetail(null);
    setIsReady(false);
    setIsNotFound(false);

    if (!sessionId || !activeClaw?.id) {
      setIsLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);

    fetchSessionDetail({ clawId: activeClaw.id, sessionId })
      .then((response) => {
        if (requestIdRef.current !== requestId) return;

        // 接口报错或返回空数据统一视为 session 不可达
        if (response.error || !response.data?.sessions?.length) {
          setIsNotFound(true);
          return;
        }

        const target = response.data.sessions.find((s) => s.id === sessionId);
        if (!target) {
          setIsNotFound(true);
          return;
        }

        setSessionDetail(target);
        setIsReady(true);

        // 同步更新 store 中 sessions 对应项，确保 activeSessionMode / activeRunPhase 等下游拿到最新数据。
        // 整对象 spread 会带上 run_phase，作为 session 切换时的初始值；live 更新走 use-subscribe-chat-state。
        const { sessions, sessionsOwnerClaw } = storeApi.getState();
        const idx = sessions.findIndex((s) => s.id === target.id);
        if (idx !== -1) {
          const updated = [...sessions];
          updated[idx] = { ...updated[idx], ...target };
          storeApi.setState({ sessions: updated });
        } else if (sessionsOwnerClaw) {
          storeApi.setState({ sessions: [target, ...sessions] });
        }
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        setIsNotFound(true);
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setIsLoading(false);
      });
  }, [sessionId, activeClaw?.id]);

  return { sessionDetail, isReady, isLoading, isNotFound };
};
